import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { User, Phone, MapPin, School, Users, HelpCircle } from "lucide-react";

const PROVINCES = ["Gauteng", "Limpopo", "Mpumalanga", "North West", "Free State", "KwaZulu-Natal", "Eastern Cape", "Western Cape", "Northern Cape"];
const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const YESNO = ["Yes", "No"];

// Every applicant field is required EXCEPT middle_name and the university portal
// login details (username/password/reference) which students may not have yet.
export const APPLICANT_LABELS = {
  first_name: "First Name", last_name: "Last Name", id_number: "ID / Passport Number",
  dob: "Date of Birth", gender: "Gender", nationality: "Nationality", home_language: "Home Language",
  email: "Email Address", phone: "Phone Number", whatsapp: "WhatsApp Number",
  street: "Street Address", suburb: "Suburb / Area", city: "City / Town", province: "Province", postal_code: "Postal Code",
  grade_status: "Your current status", school_name: "School Name", school_province: "School Province", school_year: "Year Completed / Attended",
  kin_name: "Next of Kin Full Name", kin_id: "Next of Kin ID Number", kin_gender: "Next of Kin Gender",
  kin_relationship: "Next of Kin Relationship", kin_phone: "Next of Kin Phone", kin_email: "Next of Kin Email",
  upgrading: "Upgrading your marks?", need_help: "Need help with your studies?", nsfas: "Require NSFAS funding?",
  employed: "Are you employed?", attended_before: "Attended university before?",
};

export function validateApplicant(f, { requireGrade = true } = {}) {
  const missing = [];
  for (const [k, label] of Object.entries(APPLICANT_LABELS)) {
    if (k === "grade_status" && !requireGrade) continue;
    if (!f[k] || String(f[k]).trim() === "") missing.push(label);
  }
  const email = String(f.email || "").trim().toLowerCase();
  const confirm = String(f.email_confirm || "").trim().toLowerCase();
  if (email && (!confirm || email !== confirm)) missing.push("Confirm Email Address (must match Email)");
  const nat = String(f.nationality || "").trim();
  if (nat && !/^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ .'’()/-]{1,59}$/.test(nat)) missing.push("Nationality (letters only, e.g. South African)");
  return missing;
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-slate-700 text-sm">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function SelectBox({ value, onChange, options, placeholder }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="border-slate-300"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function SectionItem({ value, icon: Icon, title, children }) {
  return (
    <AccordionItem value={value} className="bg-white border border-slate-200 rounded-lg px-5 shadow-sm">
      <AccordionTrigger className="hover:no-underline">
        <span className="flex items-center gap-3 font-display font-semibold text-[#0B2447]">
          <span className="w-8 h-8 rounded-md bg-[#0B2447]/5 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#0B2447]" />
          </span>
          {title}
        </span>
      </AccordionTrigger>
      <AccordionContent className="pt-2 pb-5">{children}</AccordionContent>
    </AccordionItem>
  );
}

export function ApplicantForm({ f, set }) {
  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">All fields marked <span className="text-red-500 font-semibold">*</span> are required. Only your Middle Name and university portal login details (if you don&rsquo;t have them yet) may be left blank.</p>
      <Accordion type="multiple" defaultValue={["personal", "contact", "school"]} className="space-y-3">
        <SectionItem value="personal" icon={User} title="1. Personal Information">
          <Grid>
            <Field label="First Name *"><Input data-testid="app-first-name" value={f.first_name || ""} onChange={set("first_name")} /></Field>
            <Field label="Middle Name"><Input value={f.middle_name || ""} onChange={set("middle_name")} /></Field>
            <Field label="Last Name *"><Input data-testid="app-last-name" value={f.last_name || ""} onChange={set("last_name")} /></Field>
            <Field label="ID / Passport Number *" hint="Used for verification and payment reference">
              <Input data-testid="app-id-number" value={f.id_number || ""} onChange={set("id_number")} />
            </Field>
            <Field label="Date of Birth *"><Input type="date" value={f.dob || ""} onChange={set("dob")} /></Field>
            <Field label="Gender *"><SelectBox value={f.gender} onChange={set("gender")} options={GENDERS} placeholder="Select gender" /></Field>
            <Field label="Nationality *"><Input value={f.nationality || ""} onChange={set("nationality")} placeholder="Enter your nationality" data-testid="applicant-nationality" maxLength={60} /></Field>
            <Field label="Home Language *"><Input value={f.home_language || ""} onChange={set("home_language")} /></Field>
          </Grid>
        </SectionItem>

        <SectionItem value="contact" icon={Phone} title="2. Contact Information">
          <Grid>
            <Field label="Email Address *" hint="We'll send application updates here">
              <Input type="email" data-testid="app-email" value={f.email || ""} onChange={set("email")} />
            </Field>
            <Field label="Confirm Email Address *" hint="Re-enter your email exactly to avoid mistakes">
              <Input type="email" data-testid="app-email-confirm" value={f.email_confirm || ""} onChange={set("email_confirm")}
                onPaste={(e) => e.preventDefault()}
                className={f.email_confirm && f.email && f.email_confirm.trim().toLowerCase() !== f.email.trim().toLowerCase() ? "border-red-400 focus-visible:ring-red-400" : ""} />
              {f.email_confirm && f.email && f.email_confirm.trim().toLowerCase() !== f.email.trim().toLowerCase() && (
                <p className="text-xs text-red-500 mt-1" data-testid="app-email-mismatch">Email addresses don&rsquo;t match.</p>
              )}
            </Field>
            <Field label="Phone Number *"><Input data-testid="app-phone" value={f.phone || ""} onChange={set("phone")} /></Field>
            <Field label="WhatsApp Number *"><Input value={f.whatsapp || ""} onChange={set("whatsapp")} /></Field>
          </Grid>
        </SectionItem>

        <SectionItem value="address" icon={MapPin} title="3. Residential Address">
          <Grid>
            <Field label="Street Address *" hint="Include house/unit number and street name"><Input value={f.street || ""} onChange={set("street")} /></Field>
            <Field label="Suburb / Area *"><Input value={f.suburb || ""} onChange={set("suburb")} /></Field>
            <Field label="City / Town *"><Input value={f.city || ""} onChange={set("city")} /></Field>
            <Field label="Province *"><SelectBox value={f.province} onChange={set("province")} options={PROVINCES} placeholder="Select province" /></Field>
            <Field label="Postal Code *"><Input value={f.postal_code || ""} onChange={set("postal_code")} /></Field>
          </Grid>
        </SectionItem>

        <SectionItem value="school" icon={School} title="4. Previous School & Grade">
          <div className="mb-4">
            <Label className="text-slate-700 text-sm">Your current status *</Label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="grade-status-group">
              {["Currently in Grade 12", "Finished school"].map((opt) => (
                <button type="button" key={opt} data-testid={`grade-${opt.includes("12") ? "g12" : "finished"}`}
                  onClick={() => set("grade_status")(opt)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${f.grade_status === opt ? "bg-[#0B2447] text-white border-[#0B2447]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <Grid>
            <Field label="School Name *"><Input value={f.school_name || ""} onChange={set("school_name")} /></Field>
            <Field label="School Province *"><SelectBox value={f.school_province} onChange={set("school_province")} options={PROVINCES} placeholder="Select province" /></Field>
            <Field label="Year Completed / Attended *"><Input value={f.school_year || ""} onChange={set("school_year")} placeholder="2025" /></Field>
          </Grid>
        </SectionItem>

        <SectionItem value="kin" icon={Users} title="5. Next of Kin / Emergency Contact">
          <Grid>
            <Field label="Full Name *"><Input value={f.kin_name || ""} onChange={set("kin_name")} /></Field>
            <Field label="ID Number *"><Input value={f.kin_id || ""} onChange={set("kin_id")} /></Field>
            <Field label="Gender *"><SelectBox value={f.kin_gender} onChange={set("kin_gender")} options={GENDERS} placeholder="Select gender" /></Field>
            <Field label="Relationship *"><Input value={f.kin_relationship || ""} onChange={set("kin_relationship")} /></Field>
            <Field label="Phone Number *"><Input value={f.kin_phone || ""} onChange={set("kin_phone")} /></Field>
            <Field label="Email *"><Input type="email" value={f.kin_email || ""} onChange={set("kin_email")} /></Field>
          </Grid>
        </SectionItem>

        <SectionItem value="additional" icon={HelpCircle} title="6. Additional Information">
          <Grid>
            <Field label="Are you currently upgrading your marks? *"><SelectBox value={f.upgrading} onChange={set("upgrading")} options={YESNO} placeholder="Select" /></Field>
            <Field label="Do you need help with your studies? *"><SelectBox value={f.need_help} onChange={set("need_help")} options={YESNO} placeholder="Select" /></Field>
            <Field label="Do you require NSFAS funding? *"><SelectBox value={f.nsfas} onChange={set("nsfas")} options={YESNO} placeholder="Select" /></Field>
            <Field label="Are you employed? *"><SelectBox value={f.employed} onChange={set("employed")} options={YESNO} placeholder="Select" /></Field>
            <Field label="Have you attended university before? *"><SelectBox value={f.attended_before} onChange={set("attended_before")} options={YESNO} placeholder="Select" /></Field>
          </Grid>
        </SectionItem>
      </Accordion>
    </div>
  );
}
